/**
 * Settings > Live Login Attempts feed.
 * Auto-refreshes every 10s. Shows recent attempts with country flag + user
 * agent + one-click "Block IP" toggle that inserts a manual lock into the
 * auth_rate_limit ledger. The main agent's rate limiter already honors
 * `*::<ip>` catch-all keys so a manual block covers every login email.
 */
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Ban, ShieldCheck, RefreshCw, Circle } from "lucide-react";
import { fetchAuthAttempts, blockIp, unblockIp } from "@/lib/api";
import { useTimezone, formatInTz } from "@/lib/tz";
import { Button } from "@/components/ui/button";

const STATUS_STYLE = {
  success: "bg-[#ECFDF5] text-[#047857]",
  failed:  "bg-[#FEF3C7] text-[#92400E]",
  locked:  "bg-[#FEE2E2] text-[#991B1B]",
};

// Very small country code → emoji flag (works for ISO-3166-1 alpha-2 codes)
function flagOf(code) {
  if (!code || typeof code !== "string" || code.length !== 2) return "";
  const base = 0x1f1e6;
  return String.fromCodePoint(
    base + code.toUpperCase().charCodeAt(0) - 65,
    base + code.toUpperCase().charCodeAt(1) - 65,
  );
}

export default function LoginAttemptsFeed() {
  const { userTz } = useTimezone();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const d = await fetchAuthAttempts(100);
      setAttempts(Array.isArray(d) ? d : []);
    } catch (e) {
      // silent — Settings page shouldn't error-toast every 10s
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, []);

  const handleBlock = async (ip) => {
    setBusy((b) => ({ ...b, [ip]: "block" }));
    try {
      await blockIp(ip, 24);
      toast.success(`Blocked ${ip} for 24h`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not block IP");
    } finally {
      setBusy((b) => ({ ...b, [ip]: null }));
    }
  };
  const handleUnblock = async (ip) => {
    setBusy((b) => ({ ...b, [ip]: "unblock" }));
    try {
      await unblockIp(ip);
      toast.success(`Unblocked ${ip}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unblock IP");
    } finally {
      setBusy((b) => ({ ...b, [ip]: null }));
    }
  };

  // Group counts for the summary strip
  const summary = useMemo(() => {
    const s = { success: 0, failed: 0, locked: 0, ips: new Set() };
    for (const a of attempts) {
      s[a.status] = (s[a.status] || 0) + 1;
      if (a.ip) s.ips.add(a.ip);
    }
    return { ...s, unique_ips: s.ips.size };
  }, [attempts]);

  return (
    <section
      data-testid="login-attempts-feed"
      className="border border-[#eaecf0] bg-white p-6 space-y-4"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-[#525252]">Live Login Attempts</div>
          <div className="text-lg font-medium tracking-tight text-[#101828]" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Last 100 attempts · updates every 10s
          </div>
          <div className="text-[10px] font-mono text-[#999] mt-1">
            {summary.success} success · {summary.failed} failed · {summary.locked} locked · {summary.unique_ips} unique IPs
          </div>
        </div>
        <Button
          variant="outline"
          onClick={load}
          className="rounded-sm h-8"
          data-testid="attempts-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
          Refresh
        </Button>
      </div>

      <div className="border border-[#eaecf0] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#eaecf0] bg-[#FAFAFA]">
              <th className="text-left px-4 py-2 overline">When</th>
              <th className="text-left px-3 py-2 overline">Status</th>
              <th className="text-left px-3 py-2 overline">Email</th>
              <th className="text-left px-3 py-2 overline">IP</th>
              <th className="text-left px-3 py-2 overline">Location</th>
              <th className="text-left px-3 py-2 overline">User agent</th>
              <th className="text-right px-4 py-2 overline">—</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-xs font-mono text-[#999]">Loading…</td></tr>
            )}
            {!loading && attempts.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-xs font-mono text-[#999]">No login attempts recorded yet.</td></tr>
            )}
            {attempts.map((a, i) => {
              const flag = flagOf(a.country);
              const style = STATUS_STYLE[a.status] || "bg-[#F4F4F5] text-[#525252]";
              return (
                <tr
                  key={`${a.ip}-${a.created_at}-${i}`}
                  className="border-b border-[#F4F4F5] hover:bg-[#FAFAFA]"
                  data-testid={`attempt-row-${i}`}
                >
                  <td className="px-4 py-2 font-mono text-xs text-[#525252] whitespace-nowrap" title={`UTC: ${a.created_at}`}>
                    {formatInTz(a.created_at, userTz, { second: "2-digit" })}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase ${style}`}>
                      <Circle className="w-1.5 h-1.5 fill-current" strokeWidth={0} />
                      {a.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-[#101828] max-w-[220px] truncate" title={a.email}>
                    {a.email || "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    <span className="text-[#101828]">{a.ip}</span>
                    {a.ip_locked && (
                      <span className="ml-1.5 inline-block text-[9px] px-1 py-0.5 rounded-sm bg-[#FEE2E2] text-[#991B1B]">LOCKED</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-[#525252]">
                    {flag && <span className="mr-1">{flag}</span>}
                    {a.country || "—"}
                    {a.city && <span className="text-[#999]"> · {a.city}</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-[#999] max-w-[240px] truncate" title={a.user_agent}>
                    {a.user_agent || "—"}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {a.ip_locked ? (
                      <button
                        onClick={() => handleUnblock(a.ip)}
                        disabled={busy[a.ip]}
                        data-testid={`unblock-${a.ip}`}
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-1 border border-[#047857] text-[#047857] hover:bg-[#047857] hover:text-white transition-colors"
                      >
                        <ShieldCheck className="w-3 h-3" strokeWidth={1.5} />
                        Unblock
                      </button>
                    ) : (
                      <button
                        onClick={() => handleBlock(a.ip)}
                        disabled={busy[a.ip]}
                        data-testid={`block-${a.ip}`}
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-1 border border-[#BE123C] text-[#BE123C] hover:bg-[#BE123C] hover:text-white transition-colors"
                      >
                        <Ban className="w-3 h-3" strokeWidth={1.5} />
                        Block 24h
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
